import React, { useState } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import useCopyWithFeedback from "../../hooks/useCopyWithFeedback";

const DocumentationPage = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || "";
  // ensure exactly one slash between base and path
  const endpoint = `${API_BASE_URL.replace(
    /\/?$/,
    "/"
  )}api/v1/connector/integration/translate`;

  const [activeTab, setActiveTab] = useState("request");
  const { copied, handleCopy } = useCopyWithFeedback();

  const exampleRequest = `{
  "languages": ["French", "Romanian"],
  "from": "English",
  "mood": "warm and romantic",
  "cultural_background": "Eastern Europe",
  "gender": "non-binary",
  "born_in": "1995",
  "message": "Good morning, my dear friend. I hope your day is filled with small joys and kind words."
}`;

  const exampleResponse = `{
  "response": [
    {
      "language": "French",
      "response": "Bonjour, mon cher ami. J’espère que ta journée sera remplie de petites joies et de mots bienveillants."
    },
    {
      "language": "Romanian",
      "response": "Bună dimineața, dragul meu prieten. Sper ca ziua ta să fie plină de bucurii mărunte și de vorbe calde."
    }
  ]
}`;

  const curlExample =
    `curl --location '${endpoint}' \\\n` +
    `  --header 'Content-Type: application/json' \\\n` +
    `  --header 'X-API-KEY: YOUR_API_KEY' \\\n` +
    `  --data '${exampleRequest.replace(/\n/g, "\\n").replace(/'/g, "\\'")}'`;

  return (
    <Container>
      <HeaderRow>
        <Title data-emotii-exclude>Translation API</Title>
        <HeaderMeta>
          <Badge tone="brand" data-emotii-exclude>v1</Badge>
          <DividerDot />
          <Small>Secure • JSON</Small>
        </HeaderMeta>
      </HeaderRow>

      <PageContent>
        <Grid>
          <Card $span2>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <Note>
              This API translates a block of text into one or more target languages in a
              single request. The response is returned synchronously - there is no job to
              poll and no file to upload.
            </Note>
            <Note style={{ marginTop: "12px" }}>
              Translations can be personalised with tone, cultural context, gender and
              birth year, so the same message can be phrased differently for different
              recipients.
            </Note>
          </Card>

          {/* API Endpoint + Auth */}
          <Card $span2>
            <EndpointRow>
              <EndpointLabel>ENDPOINT</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}</EndpointValue>
              <CopyEndpointBtn onClick={() => handleCopy("endpoint", endpoint)}>
                <CopyIcon />
                Copy
              </CopyEndpointBtn>
            </EndpointRow>

            <HeadersSection>
              <FirstHeaderRow>
                <HeadersLabel>HEADERS</HeadersLabel>
                <HeaderKey data-emotii-exclude>Content-Type</HeaderKey>
                <HeaderValue data-emotii-exclude>application/json</HeaderValue>
              </FirstHeaderRow>
              <SecondHeaderRow>
                <HeaderKey data-emotii-exclude>X-API-KEY</HeaderKey>
                <HeaderValue data-emotii-exclude>YOUR_API_KEY</HeaderValue>
              </SecondHeaderRow>
            </HeadersSection>
          </Card>

          {/* Request Body (immediately under API Endpoint) */}
          <Card $span2>
            <CardHeader>
              <CardTitle>Request Body</CardTitle>
            </CardHeader>
            <Note>
              <b>Note:</b> All parameter names and option values must be in{" "}
              <b>English</b>. The only exception is <Mono data-emotii-exclude>message</Mono>, which
              may be in any supported language.
            </Note>

            <List>
              <Item>
                <Key data-emotii-exclude>message</Key>
                <Desc>The text you want to translate.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>languages</Key>
                <Desc>
                  Target languages. Example: <Mono data-emotii-exclude>["English","French"]</Mono>
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>from</Key>
                <Desc>
                  <OptionalBadge>[Optional]</OptionalBadge> Source language. Auto-detected if omitted.
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>mood</Key>
                <Desc>
                  <OptionalBadge>[Optional]</OptionalBadge> Desired tone/style (e.g., <Mono data-emotii-exclude>"formal"</Mono>,{" "}
                  <Mono data-emotii-exclude>"romantic"</Mono>, <Mono data-emotii-exclude>"casual"</Mono>).
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>cultural_background</Key>
                <Desc>
                  <OptionalBadge>[Optional]</OptionalBadge> Regional or cultural context (e.g.,{" "}
                  <Mono data-emotii-exclude>"Japanese"</Mono>, <Mono data-emotii-exclude>"Latin American"</Mono>,{" "}
                  <Mono data-emotii-exclude>"rural US"</Mono>).
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>gender</Key>
                <Desc>
                  <OptionalBadge>[Optional]</OptionalBadge> Preferred gender style. Supported:{" "}
                  <Mono data-emotii-exclude>"male"</Mono>, <Mono data-emotii-exclude>"female"</Mono>,{" "}
                  <Mono data-emotii-exclude>"neutral"</Mono>, <Mono data-emotii-exclude>"non-binary"</Mono>.
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>born_in</Key>
                <Desc>
                  <OptionalBadge>[Optional]</OptionalBadge> Birth year of the recipient. Example:{" "}
                  <Mono data-emotii-exclude>"2010"</Mono>.
                </Desc>
              </Item>
            </List>
          </Card>

          {/* Examples */}
          <Card $span2>
            <CardHeader>
              <CardTitle>Examples</CardTitle>
              <Tabs>
                <Tab
                  aria-selected={activeTab === "request"}
                  onClick={() => setActiveTab("request")}
                >
                  Request
                </Tab>
                <Tab
                  aria-selected={activeTab === "response"}
                  onClick={() => setActiveTab("response")}
                >
                  Response
                </Tab>
                <Tab
                  aria-selected={activeTab === "curl"}
                  onClick={() => setActiveTab("curl")}
                >
                  cURL
                </Tab>
              </Tabs>
            </CardHeader>

            {activeTab === "request" && (
              <CodeBlock>
                <Toolbar>
                  <LangPill data-emotii-exclude>JSON</LangPill>
                  <Spacer />
                  <CopyBtn
                    onClick={() => handleCopy("req", exampleRequest)}
                    data-copied={copied === "req" ? "true" : "false"}
                  >
                    <CopyIcon />
                    {copied === "req" ? "Copied" : "Copy"}
                  </CopyBtn>
                </Toolbar>
                <CodeArea role="region" aria-label="Example Request Body" data-emotii-exclude>
                  <code>
                    {`{
  "`}
                    <Red>languages</Red>
                    {`": [`}
                    <Purple>"French"</Purple>
                    {`, `}
                    <Purple>"Romanian"</Purple>
                    {`],
  "`}
                    <Red>from</Red>
                    {`": "`}
                    <Purple>English</Purple>
                    {`",
  "`}
                    <Red>mood</Red>
                    {`": "`}
                    <Purple>warm and romantic</Purple>
                    {`",
  "`}
                    <Red>cultural_background</Red>
                    {`": "`}
                    <Purple>Eastern Europe</Purple>
                    {`",
  "`}
                    <Red>gender</Red>
                    {`": "`}
                    <Purple>non-binary</Purple>
                    {`",
  "`}
                    <Red>born_in</Red>
                    {`": "`}
                    <Purple>1995</Purple>
                    {`",
  "`}
                    <Red>message</Red>
                    {`": "`}
                    <Purple>
                      Good morning, my dear friend. I hope your day is filled
                      with small joys and kind words.
                    </Purple>
                    {`"
}`}
                  </code>
                </CodeArea>
              </CodeBlock>
            )}

            {activeTab === "response" && (
              <CodeBlock>
                <Toolbar>
                  <LangPill data-emotii-exclude>JSON</LangPill>
                  <Spacer />
                  <CopyBtn
                    onClick={() => handleCopy("res", exampleResponse)}
                    data-copied={copied === "res" ? "true" : "false"}
                  >
                    <CopyIcon />
                    {copied === "res" ? "Copied" : "Copy"}
                  </CopyBtn>
                </Toolbar>
                <CodeArea role="region" aria-label="Example Response" data-emotii-exclude>
                  <code>
                    {`{
  "`}
                    <Red>response</Red>
                    {`": [
    {
      `}
                    <Purple>"language": "French",</Purple>
                    {`
      `}
                    <Purple>
                      "response": "Bonjour, mon cher ami. J’espère que ta
                      journée sera remplie de petites joies et de mots
                      bienveillants."
                    </Purple>
                    {`
    },
    {
      `}
                    <Purple>"language": "Romanian",</Purple>
                    {`
      `}
                    <Purple>
                      "response": "Bună dimineața, dragul meu prieten. Sper ca
                      ziua ta să fie plină de bucurii mărunte și de vorbe
                      calde."
                    </Purple>
                    {`
    }
  ]
}`}
                  </code>
                </CodeArea>
              </CodeBlock>
            )}

            {activeTab === "curl" && (
              <CodeBlock>
                <Toolbar>
                  <LangPill data-emotii-exclude>bash</LangPill>
                  <Spacer />
                  <CopyBtn
                    onClick={() => handleCopy("curl", curlExample)}
                    data-copied={copied === "curl" ? "true" : "false"}
                  >
                    <CopyIcon />
                    {copied === "curl" ? "Copied" : "Copy"}
                  </CopyBtn>
                </Toolbar>
                <CodeArea role="region" aria-label="curl Example" data-emotii-exclude>
                  <code>{curlExample}</code>
                </CodeArea>
              </CodeBlock>
            )}
          </Card>

          {/* Error Handling */}
          <Card $span2>
            <CardHeader>
              <CardTitle>Error Handling</CardTitle>
            </CardHeader>
            <ErrorList>
              <ErrorRow>
                <ErrCode $tone="warn" data-emotii-exclude>400</ErrCode>
                <ErrText>
                  Bad Request — invalid JSON or missing required fields.
                </ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode $tone="danger" data-emotii-exclude>401</ErrCode>
                <ErrText>
                  Unauthorized — missing or invalid <Mono data-emotii-exclude>X-API-KEY</Mono>.
                </ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode $tone="neutral" data-emotii-exclude>500</ErrCode>
                <ErrText>
                  Internal Server Error — something went wrong server-side.
                </ErrText>
              </ErrorRow>
            </ErrorList>
          </Card>
        </Grid>
      </PageContent>
    </Container>
  );
};

export default DocumentationPage;

/* ---------------- Styles ---------------- */

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
  width: 100%;
  background: #fafafa;
  overflow: hidden; /* header fixed, content scrolls */
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 1; /* keep visible */
`;

const Title = styled.h2`
  color: ${lightThemeColors.darkColor};
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  margin: 0;
`;

const HeaderMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const Small = styled.span`
  font-size: 12px;
  color: rgba(56, 56, 58, 0.7);
`;

const DividerDot = styled.span`
  width: 4px;
  height: 4px;
  background: rgba(56, 56, 58, 0.35);
  border-radius: 50%;
  display: inline-block;
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${(props) =>
    props.tone === "brand"
      ? lightThemeColors.primaryColor
      : "rgba(56,56,58,0.1)"};
  color: ${(props) =>
    props.tone === "brand" ? "#fff" : lightThemeColors.darkColor};
`;

const PageContent = styled.div`
  overflow: auto; /* scrollable area */
  padding: 24px;
`;

const Grid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(12, 1fr);
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  border-radius: 14px;
  padding: 18px;
  grid-column: ${(props) => (props.$span2 ? "span 12" : "span 6")};

  @media (max-width: 1024px) {
    grid-column: 1 / -1;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const CardTitle = styled.h3`
  color: ${lightThemeColors.darkColor};
  font-size: 16px;
  font-weight: 800;
  margin: 0;
`;

const MethodBadge = styled.span`
  background: rgba(72, 165, 240, 0.12);
  color: rgba(72, 165, 240, 1);
  font-weight: 800;
  letter-spacing: 0.4px;
  border: 1px solid rgba(72, 165, 240, 0.25);
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 11px;
  text-transform: uppercase;
`;

const KV = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 14px;
  align-items: top;
  padding: 10px 0;
  border-bottom: ${(props) =>
    props.$noDivider ? "none" : "1px dashed rgba(0,0,0,0.06)"};
`;

const Label = styled.div`
  color: rgba(56, 56, 58, 0.7);
  font-size: 12px;
  font-weight: 700;
`;

const Value = styled.div`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 700;
  word-break: break-all;
`;

const KeyValueRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  width: 100%;
`;

const Chip = styled.span`
  background: #f5f5f5;
  color: ${lightThemeColors.darkColor};
  padding: 6px 10px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  font-weight: 600;
  min-width: 118px;
  text-align: left;
`;

const KVValue = styled.span`
  color: ${lightThemeColors.primaryColor};
  font-weight: 700;
  font-size: 13px;
`;

const Note = styled.p`
  color: rgba(56, 56, 58, 0.75);
  font-size: 13px;
  line-height: 1.6;
  margin: 4px 0 14px;
`;

const List = styled.div`
  display: grid;
  gap: 10px;
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Key = styled.div`
  color: ${lightThemeColors.darkColor};
  font-weight: 700;
  font-size: 13px;
  background: #f5f5f5;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  width: fit-content;
`;

const Desc = styled.div`
  color: rgba(56, 56, 58, 0.9);
  font-size: 13px;
  line-height: 1.6;
`;

const OptionalBadge = styled.span`
  color: #059669;
  font-weight: 600;
`;

const Tabs = styled.div`
  display: inline-flex;
  background: rgba(56, 56, 58, 0.06);
  border-radius: 10px;
  padding: 4px;
  gap: 4px;
`;

const Tab = styled.button`
  border: none;
  background: transparent;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
  color: ${lightThemeColors.darkColor};
  cursor: pointer;
  transition: all 0.15s ease;

  &[aria-selected="true"] {
    background: #fff;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  }
`;

const CodeBlock = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(34, 39, 46, 1);
  color: #fff;
  border-radius: 12px;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
`;

const LangPill = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  background: rgba(72, 165, 240, 0.18);
  color: rgba(72, 165, 240, 1);
  border: 1px solid rgba(72, 165, 240, 0.35);
  padding: 4px 8px;
  border-radius: 999px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const CopyBtn = styled.button`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &[data-copied="true"] {
    background: rgba(72, 165, 240, 0.2);
    border-color: rgba(72, 165, 240, 0.5);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CodeArea = styled.pre`
  font-family: "Menlo", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 12.5px;
  line-height: 1.45;
  padding: 14px;
  margin: 0;
  max-width: 100%;
  overflow: auto;
  white-space: pre;
  word-break: normal;

  code {
    font-family: inherit;
  }
`;

const ErrorList = styled.div`
  display: grid;
  gap: 10px;
`;

const ErrorRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ErrCode = styled.div`
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.3px;
  border-radius: 6px;
  padding: 6px 10px;
  text-align: center;
  width: fit-content;

  color: ${(props) =>
    props.$tone === "danger"
      ? "#991b1b"
      : props.$tone === "warn"
      ? "#991b1b"
      : "#991b1b"};
  background: ${(props) =>
    props.$tone === "danger"
      ? "#fee2e2"
      : props.$tone === "warn"
      ? "#fee2e2"
      : "#fee2e2"};
  border: 1px solid
    ${(props) =>
      props.$tone === "danger"
        ? "#fecaca"
        : props.$tone === "warn"
        ? "#fecaca"
        : "#fecaca"};
`;

const ErrText = styled.div`
  color: rgba(56, 56, 58, 0.9);
  font-size: 13px;
`;

const Mono = styled.code`
  font-family: "Menlo", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 0.95em;
`;

const Red = styled.span`
  color: rgba(252, 115, 76);
  font-family: Menlo, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
`;

const Purple = styled.span`
  color: rgba(72, 165, 240);
  font-family: Menlo, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
`;

const EndpointRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const EndpointLabel = styled.div`
  color: #059669;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  background: #d1fae5;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #a7f3d0;
`;

const EndpointValue = styled.div`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 600;
  word-break: break-all;
  font-family: "Menlo", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
`;

const HeadersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FirstHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SecondHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: 100px; /* Indent to align with Content-Type */
`;

const HeadersLabel = styled.div`
  color: #d97706;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: #fed7aa;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #fdba74;
  width: fit-content;
`;


const HeaderKey = styled.div`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 600;
  background: #f5f5f5;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  width: fit-content;
`;

const HeaderValue = styled.div`
  color: #000;
  font-size: 14px;
  font-weight: 400;
`;


const CopyEndpointBtn = styled.button`
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  color: #374151;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: #e5e7eb;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CopyIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3.33333 10H2.66667C2.31304 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35943 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31304 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31304 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35943 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31304 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
